import React, {FunctionComponent, MouseEvent, useCallback} from 'react';
import {
    Box,
    ButtonBase,
    Tooltip,
    Typography,
    useMediaQuery,
    type Theme,
} from '@mui/material';
import TravelExploreRounded from '@mui/icons-material/TravelExploreRounded';
import WidgetsRounded from '@mui/icons-material/WidgetsRounded';
import {useTranslation} from 'react-i18next';
import type {AppBarSearchMode} from './types';

export interface AppBarSearchModeTogglerProps {
    mode: AppBarSearchMode;
    onChange: (mode: AppBarSearchMode) => void;
    disabled?: boolean;
}

type ModeOptionProps = {
    value: AppBarSearchMode;
    selected: boolean;
    disabled?: boolean;
    showLabel: boolean;
    label: string;
    icon: React.ReactNode;
    onSelect: (mode: AppBarSearchMode) => void;
};

const ModeOption: FunctionComponent<ModeOptionProps> = ({
    value,
    selected,
    disabled,
    showLabel,
    label,
    icon,
    onSelect,
}) => {
    const button = (
        <ButtonBase
            disabled={disabled}
            onClick={() => onSelect(value)}
            aria-pressed={selected}
            aria-label={label}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                minWidth: showLabel ? 68 : 34,
                height: 28,
                px: showLabel ? 1 : 0.75,
                borderRadius: 1,
                // Explicit colors so InputAdornment / TextField inheritance cannot
                // tint Nexus vs System differently (primary when selected, secondary when not).
                color: selected ? 'primary.contrastText' : 'text.secondary',
                bgcolor: selected ? 'primary.main' : 'transparent',
                transition: (theme) => theme.transitions.create(['background-color', 'color', 'box-shadow'], {
                    duration: theme.transitions.duration.shorter,
                }),
                boxShadow: selected ? 1 : 0,
                '&:hover': {
                    bgcolor: selected ? 'primary.dark' : 'action.selected',
                },
                '&.Mui-disabled': {
                    opacity: 0.45,
                },
                '& .MuiSvgIcon-root': {
                    color: 'inherit',
                },
            }}
        >
            {icon}
            {showLabel && (
                <Typography
                    variant="caption"
                    component="span"
                    fontWeight={700}
                    lineHeight={1}
                    noWrap
                    sx={{color: 'inherit'}}
                >
                    {label}
                </Typography>
            )}
        </ButtonBase>
    );

    if (showLabel) {
        return button;
    }

    return <Tooltip title={label}>{button}</Tooltip>;
};

/**
 * Segmented control for switching between Nexus and System search.
 * Rendered as a TextField endAdornment (icon-only ≤`lg`; labels from `xl` up).
 */
const AppBarSearchModeToggler: FunctionComponent<AppBarSearchModeTogglerProps> = ({
    mode,
    onChange,
    disabled = false,
}) => {
    const showLabels = useMediaQuery((t: Theme) => t.breakpoints.up('xl'));
    const {t} = useTranslation('components', {keyPrefix: 'panel-sections.app-bar.search-mode'});

    /**
     * Prevents the parent TextField / Autocomplete from stealing the click
     * (e.g. opening the command palette or focusing the Nexus input).
     */
    const stopFieldInteraction = useCallback((event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    /**
     * Applies the selected mode when it differs from the current one.
     */
    const handleSelect = useCallback((next: AppBarSearchMode) => {
        if (disabled || next === mode) {
            return;
        }
        onChange(next);
    }, [disabled, mode, onChange]);

    return (
        <Box
            component="span"
            role="group"
            aria-label={t($ => $.ariaLabel)}
            onClick={stopFieldInteraction}
            onMouseDown={stopFieldInteraction}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                gap: 0.25,
                p: 0.375,
                borderRadius: 1,
                bgcolor: 'action.hover',
                color: 'text.secondary',
            }}
        >
            <ModeOption
                value="nexus"
                selected={mode === 'nexus'}
                disabled={disabled}
                showLabel={showLabels}
                label={t($ => $.nexus)}
                icon={<TravelExploreRounded sx={{fontSize: 20}}/>}
                onSelect={handleSelect}
            />
            <ModeOption
                value="system"
                selected={mode === 'system'}
                disabled={disabled}
                showLabel={showLabels}
                label={t($ => $.system)}
                icon={<WidgetsRounded sx={{fontSize: 20}}/>}
                onSelect={handleSelect}
            />
        </Box>
    );
};

export default AppBarSearchModeToggler;
